import React, {FC, useState} from 'react'
import {createPortal} from 'react-dom'
import HourOnlyInput from '../../components/HourOnlyInput'
import InputField from '../../components/InputField'
import {addProductsDiscount} from '../core/_requests'
import {UserModel} from '../../../auth'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {prepareErrors} from '../../../errors/core/_models'

type IProps = {
  onCloseModal: () => void
  language: string
  currentUser: UserModel | undefined
  products_id: number[]
}

const SpecialSelectionModal: FC<IProps> = ({onCloseModal, language, currentUser, products_id}) => {
  const {formatMessage} = useIntl()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [apiMessage, setApiMessage] = useState<{text: string; type: 'error' | 'partial'} | null>(null)
  const currentDate = new Date().toISOString().split('T')[0]
  const [form, setForm] = useState({
    date_start: {
      error: false,
      value: '',
      msg: '',
    },
    date_end: {
      error: false,
      value: '',
      msg: '',
    },
    quantity: {
      error: false,
      value: '',
      msg: '',
    },
    sale_discount_percentage: {
      error: false,
      value: '',
      msg: '',
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | {target: {id: string; value: string}}) => {
    const {id, value} = e.target
    setForm({...form, [id]: {value: value}})
  }

  // Sale discount % accepts whole numbers under 100 only: strip everything
  // that isn't a digit (blocks '.', ',', 'e', '-', …) and cap at 2 digits
  // so the value can never reach 100.
  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 2)
    setForm((prev) => ({...prev, sale_discount_percentage: {...prev.sale_discount_percentage, value: digits}}))
  }

  const isFormHasError = () => {
    let hasError = false
    setIsLoading(true)

    setForm((prev: any) => ({
      ...prev,
      date_start: {...prev.date_start, error: false, msg: ''},
      date_end: {...prev.date_end, error: false, msg: ''},
      quantity: {...prev.quantity, error: false},
      sale_discount_percentage: {...prev.sale_discount_percentage, error: false, msg: ''},
    }))

    if (form.date_start.value === '') {
      setForm((prev: any) => ({
        ...prev,
        date_start: {...prev.date_start, error: true},
      }))
      hasError = true
    }
    if (form.date_end.value === '') {
      setForm((prev: any) => ({
        ...prev,
        date_end: {...prev.date_end, error: true},
      }))
      hasError = true
    }
    if (form.quantity.value === '') {
      setForm((prev: any) => ({
        ...prev,
        quantity: {...prev.quantity, error: true},
      }))
      hasError = true
    }
    if (form.sale_discount_percentage.value === '') {
      setForm((prev: any) => ({
        ...prev,
        sale_discount_percentage: {...prev.sale_discount_percentage, error: true, msg: ''},
      }))
      hasError = true
    } else if (!/^\d+$/.test(String(form.sale_discount_percentage.value).trim())) {
      // Whole numbers only — reject decimals / negatives / non-numerics.
      setForm((prev: any) => ({
        ...prev,
        sale_discount_percentage: {
          ...prev.sale_discount_percentage,
          error: true,
          msg: formatMessage({id: 'sale_discount_integer_error'}),
        },
      }))
      hasError = true
    }
    setIsLoading(false)
    return hasError
  }

  const handleSubmit = async () => {
    if (!isFormHasError()) {
      setIsLoading(true)
      setApiMessage(null)
      try {
        const data = await addProductsDiscount(currentUser?.token, language, {
          products_id: products_id.map((id) => id.toString()),
          date_start: form.date_start.value,
          date_end: form.date_end.value,
          quantity: +form.quantity.value,
          sale_discount_percentage: +form.sale_discount_percentage.value,
        })

        if (data?.data?.status === 'error') {
          setApiMessage({text: data.data.message, type: 'error'})
        } else if (data?.data?.status === 'partial') {
          setApiMessage({text: data.data.message, type: 'partial'})
        } else {
          navigate('/discount/pending-special-products?from=/discount/add-selection', {replace: true})
        }
        setIsLoading(false)
      } catch (exp: any) {
        setIsLoading(false)
        const updatedState: any = prepareErrors(exp, form)
        if (updatedState) setForm(updatedState)
      }
    }
  }

  return createPortal(
    <div className='aos-modal-overlay' onClick={onCloseModal}>
      <div className='aos-modal' onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true'>
        <div className='aos-modal-head'>
          <h3 className='aos-modal-title'>{formatMessage({id: 'add_discount_selected_prods'})}</h3>
          <button type='button' className='aos-modal-close' onClick={onCloseModal} aria-label='Close'>
            <i className='fas fa-xmark' aria-hidden='true' />
          </button>
        </div>

        <div className='aos-modal-body'>
          {/* Date Start */}
          <div className='aos-field'>
            <label className='aos-label'>
              {formatMessage({id: 'date_start'})} <span className='aos-req'>*</span>
            </label>
            <HourOnlyInput
              id='date_start'
              value={form.date_start.value}
              isRequired={true}
              hasError={form.date_start.error}
              onChange={handleChange}
              min={currentDate}
              showLabel={false}
              errorMsg={form.date_start.msg || formatMessage({id: 'date_start_required'})}
            />
          </div>

          {/* Date End */}
          <div className='aos-field'>
            <label className='aos-label'>
              {formatMessage({id: 'date_end'})} <span className='aos-req'>*</span>
              <span className='aos-tooltip'>
                <i className='fas fa-circle-info' aria-hidden='true' />
                <span className='aos-tip'>{formatMessage({id: 'discount_end_tooltip'})}</span>
              </span>
            </label>
            <HourOnlyInput
              id='date_end'
              value={form.date_end.value}
              isRequired={true}
              hasError={form.date_end.error}
              onChange={handleChange}
              min={currentDate}
              showLabel={false}
              errorMsg={form.date_end.msg || formatMessage({id: 'date_end_required'})}
            />
          </div>

          {/* Quantity */}
          <div className='aos-field'>
            <label className='aos-label'>
              {formatMessage({id: 'quantity'})} <span className='aos-req'>*</span>
            </label>
            <InputField
              type='number'
              id='quantity'
              value={form.quantity.value}
              isRequired={true}
              hasError={form.quantity.error}
              onChange={handleChange}
              min='0'
              showLabel={false}
              errorMsg={form.quantity.msg || formatMessage({id: 'SPECIAL.special_product_quantity_required'})}
            />
          </div>

          {/* Sale discount percentage */}
          <div className='aos-field'>
            <label className='aos-label'>
              {formatMessage({id: 'sale_discount_percentage'})} <span className='aos-req'>*</span>
            </label>
            <InputField
              type='text'
              inputMode='numeric'
              maxLength={2}
              id='sale_discount_percentage'
              value={form.sale_discount_percentage.value}
              isRequired={true}
              hasError={form.sale_discount_percentage.error}
              onChange={handlePercentageChange}
              placeholder='e.g. 30'
              showLabel={false}
              errorMsg={form.sale_discount_percentage.msg || formatMessage({id: 'SPECIAL.sale_discount_percentage_required'})}
            />
            <p className='aos-hint'>{formatMessage({id: 'sale_discount_integer_hint'})}</p>
          </div>
        </div>

        {apiMessage && (
          <div className={`aos-api-msg ${apiMessage.type === 'error' ? 'aos-api-msg--error' : 'aos-api-msg--partial'}`}>
            <i className={`fas ${apiMessage.type === 'error' ? 'fa-circle-xmark' : 'fa-circle-info'}`} aria-hidden='true' />
            {apiMessage.text}
          </div>
        )}

        <div className='aos-modal-foot'>
          <button type='button' className='aos-btn aos-btn-outline' onClick={onCloseModal}>
            {formatMessage({id: 'close'})}
          </button>
          <button type='button' className='aos-btn aos-btn-primary' disabled={isLoading} onClick={handleSubmit}>
            {formatMessage({id: 'apply'})}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default SpecialSelectionModal
